import * as React from 'react'
import {ChangeEvent, Component, ReactElement} from 'react'
import {Box} from '@mui/material'
import {NatApplicationRepo} from '../../repository/NatApplicationRepo'
import {BlockSpacer, currentMedia, GridContainer, GridItem} from '../../../../../styledComponents/ResponsiveConvenience'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {convertToRoomNumberForResident} from '../../../../common/util/Natutil'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import moment from 'moment'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {ApplyButton, ItemHeading, WideWhiteArea} from '../../common/resident/NatResidentApplicationsCommon'
import {NatGetCurrentDateTime} from '../../../currentDateTime/repository/NatGetCurrentDateTime'
import {Nullable} from '../../../../../app/TypeHelpers'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import NatResidentApplicationDetailFormItem from '../../common/resident/NatResidentApplicationDetailFormItem'
import HolidaysRepository from '../../../../../components/HolidaysRepository'
import styled from 'styled-components'
import {DESTINATION_TYPE} from '../../../../../CodeMaster'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import PPRadioGroupForResident from '../../../../../components/shared/forms/resident/PPRadioGroupForResident'
import PPDatePickerForResident from '../../../../../components/shared/forms/resident/PPDatePickerForResident'
import {HKConstants} from '../../../../../HKConstants'
import {NatResidentLongAbsenceLocationState} from '../NatResidentApplicationInterface'
import {NatPutLongAbsenceRequest} from '../../repository/NatPutLongAbsence'
import ErrorMessageAreaWrapper from '../../../../../components/shared/forms/resident/ErrorMessageAreaWrapper'
import {DefaultStyledButton} from '../../../../../styledComponents/Button'
import {NatZipCode} from '../../../condominium/repository/NatZipCode'
import {RequestItemError} from '../../../../../network/response/RequestItemError'
import {Messages} from '../../../../../Messages'
import {PPTextAreaForResident} from '../../../../../components/shared/forms/resident/PPTextAreaForResident'
import {Colors, FontSize} from '../../../../../styledComponents/Constants'
import {DisplayItemInputTitleRequiredNoMargin} from '../../../../../styledComponents/DisplayItem'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    holidaysRepo: HolidaysRepository
    navigate: NavigateFunction
    location: Location<NatResidentLongAbsenceLocationState>
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType

    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    absenceStartDate: Nullable<string>
    absenceEndDate: Nullable<string>
    applicantContactPhoneNumber: Nullable<string>
    destinationType: Nullable<string>
    destinationZipCode: Nullable<string>
    destinationPrefecture: Nullable<string>
    destinationAddress2: Nullable<string>
    destinationAddress3: Nullable<string>
    destinationBuildingRoomNumber: Nullable<string>
    destinationAddress: Nullable<string>
    contactFamilyName: Nullable<string>
    contactFirstName: Nullable<string>
    contactRelationship: Nullable<string>
    contactZipCode: Nullable<string>
    contactPrefecture: Nullable<string>
    contactAddress2: Nullable<string>
    contactAddress3: Nullable<string>
    contactBuildingRoomNumber: Nullable<string>
    contactPhoneNumber: Nullable<string>

    submitButtonLabel: string
    onClickApplyConfirmButton: (request: NatPutLongAbsenceRequest) => Promise<void>
}

interface State {
    applicantFamilyName: string
    applicantFirstName: string
    absenceStartDate: string
    absenceEndDate: string
    applicantContactPhoneNumber: string
    destinationType: string
    destinationZipCode: string
    destinationPrefecture: string
    destinationAddress2: string
    destinationAddress3: string
    destinationBuildingRoomNumber: string
    destinationAddress: string
    contactFamilyName: string
    contactFirstName: string
    contactRelationship: string
    contactZipCode: string
    contactPrefecture: string
    contactAddress2: string
    contactAddress3: string
    contactBuildingRoomNumber: string
    contactPhoneNumber: string

    currentDate: moment.Moment
    holidays: string[]

    applyButtonDisabled: boolean
    isLoadCompleted: boolean
}

export default class NatResidentLongAbsenceForm extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            applyButtonDisabled: true,
            applicantFamilyName: '',
            applicantFirstName: '',
            absenceStartDate: '',
            absenceEndDate: '',
            applicantContactPhoneNumber: '',
            destinationType: DESTINATION_TYPE.DOMESTIC.value,
            destinationZipCode: '',
            destinationPrefecture: '',
            destinationAddress2: '',
            destinationAddress3: '',
            destinationBuildingRoomNumber: '',
            destinationAddress: '',
            contactFamilyName: '',
            contactFirstName: '',
            contactRelationship: '',
            contactZipCode: '',
            contactPrefecture: '',
            contactAddress2: '',
            contactAddress3: '',
            contactBuildingRoomNumber: '',
            contactPhoneNumber: '',
            currentDate: moment(),
            holidays: [],
            isLoadCompleted: false,
        }

        this.handleChangeText = this.handleChangeText.bind(this)
        this.handleChangeAbsenceStartDate = this.handleChangeAbsenceStartDate.bind(this)
        this.handleChangeAbsenceEndDate = this.handleChangeAbsenceEndDate.bind(this)
        this.handleChangeDestinationType = this.handleChangeDestinationType.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const [currentTimeResponse, holidays] = await Promise.all([
                this.getCurrentDateTime(),
                this.getHoliday(),
            ])

            if (currentTimeResponse && currentTimeResponse.details) {
                this.setState({
                    currentDate: moment(currentTimeResponse.details.currentDateTime),
                })
            }

            const applicantFamilyName = this.props.applicantFamilyName ?? this.state.applicantFamilyName
            const applicantFirstName = this.props.applicantFirstName ?? this.state.applicantFirstName
            const absenceStartDate = this.props.absenceStartDate ?? this.state.absenceStartDate
            const absenceEndDate = this.props.absenceEndDate ?? this.state.absenceEndDate
            const applicantContactPhoneNumber =
                this.props.applicantContactPhoneNumber ?? this.state.applicantContactPhoneNumber
            const destinationType = this.props.destinationType ?? this.state.destinationType
            const destinationZipCode = this.props.destinationZipCode ?? this.state.destinationZipCode
            const destinationPrefecture = this.props.destinationPrefecture ?? this.state.destinationPrefecture
            const destinationAddress2 = this.props.destinationAddress2 ?? this.state.destinationAddress2
            const destinationAddress3 = this.props.destinationAddress3 ?? this.state.destinationAddress3
            const destinationBuildingRoomNumber =
                this.props.destinationBuildingRoomNumber ?? this.state.destinationBuildingRoomNumber
            const destinationAddress = this.props.destinationAddress ?? this.state.destinationAddress
            const contactFamilyName = this.props.contactFamilyName ?? this.state.contactFamilyName
            const contactFirstName = this.props.contactFirstName ?? this.state.contactFirstName
            const contactRelationship = this.props.contactRelationship ?? this.state.contactRelationship
            const contactZipCode = this.props.contactZipCode ?? this.state.contactZipCode
            const contactPrefecture = this.props.contactPrefecture ?? this.state.contactPrefecture
            const contactAddress2 = this.props.contactAddress2 ?? this.state.contactAddress2
            const contactAddress3 = this.props.contactAddress3 ?? this.state.contactAddress3
            const contactBuildingRoomNumber =
                this.props.contactBuildingRoomNumber ?? this.state.contactBuildingRoomNumber
            const contactPhoneNumber = this.props.contactPhoneNumber ?? this.state.contactPhoneNumber
            this.setState({
                applicantFamilyName: applicantFamilyName,
                applicantFirstName: applicantFirstName,
                absenceStartDate: absenceStartDate,
                absenceEndDate: absenceEndDate,
                applicantContactPhoneNumber: applicantContactPhoneNumber,
                destinationType: destinationType,
                destinationZipCode: destinationZipCode,
                destinationPrefecture: destinationPrefecture,
                destinationAddress2: destinationAddress2,
                destinationAddress3: destinationAddress3,
                destinationBuildingRoomNumber: destinationBuildingRoomNumber,
                destinationAddress: destinationAddress,
                contactFamilyName: contactFamilyName,
                contactFirstName: contactFirstName,
                contactRelationship: contactRelationship,
                contactZipCode: contactZipCode,
                contactPrefecture: contactPrefecture,
                contactAddress2: contactAddress2,
                contactAddress3: contactAddress3,
                contactBuildingRoomNumber: contactBuildingRoomNumber,
                contactPhoneNumber: contactPhoneNumber,
                holidays: holidays === null ? [] : holidays,
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
        }
        this.props.eventEmitter.closeLoading()
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        return await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async getHoliday(): Promise<Nullable<string[]>> {
        const {holidaysRepo, eventEmitter} = this.props

        const minDate: string = HKConstants.CMN.SYC.MIN_HOLIDAY_DATE
        const maxDate: string = HKConstants.CMN.SYC.MAX_HOLIDAY_DATE
        return await this.props.asyncCallFunction(
            holidaysRepo.getHolidays.bind(holidaysRepo, minDate, maxDate),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeAbsenceStartDate(date: string): void {
        this.setState({absenceStartDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeAbsenceEndDate(date: string): void {
        this.setState({absenceEndDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeDestinationType(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({
            destinationType: event.target.value,
        }, () => {
            this.updateInitChangeContractorRequest()
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateInitChangeContractorRequest(): void {
        const {destinationType} = this.state
        if (destinationType === DESTINATION_TYPE.DOMESTIC.value) {
            this.setState({
                destinationAddress: '',
                contactFamilyName: '',
                contactFirstName: '',
                contactRelationship: '',
                contactZipCode: '',
                contactPrefecture: '',
                contactAddress2: '',
                contactAddress3: '',
                contactBuildingRoomNumber: '',
                contactPhoneNumber: '',
            })
        }
        if (destinationType === DESTINATION_TYPE.OVERSEA.value) {
            this.setState({
                destinationZipCode: '',
                destinationPrefecture: '',
                destinationAddress2: '',
                destinationAddress3: '',
                destinationBuildingRoomNumber: '',
            })
        }
    }

    updateApplyConfirmButtonDisabled(): void {
        const {
            applicantFamilyName, applicantFirstName, absenceStartDate, absenceEndDate,
            applicantContactPhoneNumber, destinationType, destinationPrefecture, destinationAddress2,
            destinationAddress3, contactFamilyName, contactFirstName, contactRelationship,
            contactPrefecture, contactAddress2, contactAddress3, contactPhoneNumber,
        } = this.state

        let applyButtonDisabled = false

        if (!applicantFamilyName || !applicantFirstName || !absenceStartDate ||
            !absenceEndDate || !applicantContactPhoneNumber || !destinationType) {
            applyButtonDisabled = true
        }

        if (destinationType === DESTINATION_TYPE.DOMESTIC.value) {
            if (!destinationPrefecture || !destinationAddress2 || !destinationAddress3) {
                applyButtonDisabled = true
            }
        }

        if (destinationType === DESTINATION_TYPE.OVERSEA.value) {
            if (!contactFamilyName || !contactFirstName || !contactRelationship ||
                !contactPrefecture || !contactAddress2 || !contactAddress3 || !contactPhoneNumber) {
                applyButtonDisabled = true
            }
        }

        this.setState({applyButtonDisabled: applyButtonDisabled})
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            applicantFamilyName, applicantFirstName, absenceStartDate, absenceEndDate,
            applicantContactPhoneNumber, destinationType, destinationZipCode, destinationPrefecture,
            destinationAddress2, destinationAddress3, destinationBuildingRoomNumber, destinationAddress,
            contactFamilyName, contactFirstName, contactRelationship, contactZipCode,
            contactPrefecture, contactAddress2, contactAddress3, contactBuildingRoomNumber,
            contactPhoneNumber,
        } = this.state

        const request: NatPutLongAbsenceRequest = {
            applicantFamilyName: applicantFamilyName,
            applicantFirstName: applicantFirstName,
            absenceStartDate: absenceStartDate,
            absenceEndDate: absenceEndDate,
            applicantContactPhoneNumber: applicantContactPhoneNumber,
            destinationType: destinationType,
            destinationZipCode: destinationZipCode || null,
            destinationPrefecture: destinationPrefecture || null,
            destinationAddress2: destinationAddress2 || null,
            destinationAddress3: destinationAddress3 || null,
            destinationBuildingRoomNumber: destinationBuildingRoomNumber || null,
            destinationAddress: destinationAddress || null,
            contactFamilyName: contactFamilyName || null,
            contactFirstName: contactFirstName || null,
            contactRelationship: contactRelationship || null,
            contactZipCode: contactZipCode || null,
            contactPrefecture: contactPrefecture || null,
            contactAddress2: contactAddress2 || null,
            contactAddress3: contactAddress3 || null,
            contactBuildingRoomNumber: contactBuildingRoomNumber || null,
            contactPhoneNumber: contactPhoneNumber || null,
            confirmed: false,
        }

        if (!request) {
            return
        }
        await this.props.onClickApplyConfirmButton(request)
    }

    async getDomesticAddressFromZipCode(zipCode: string): Promise<void> {
        const zipCodeResponse = await this.getAddressFromZipcode(zipCode, 'destinationZipCode')
        if (zipCodeResponse && zipCodeResponse.data
            && zipCodeResponse.data[0] && zipCodeResponse.data[0].ja) {
            this.setState({
                destinationPrefecture: zipCodeResponse.data[0].ja.prefecture,
                destinationAddress2: zipCodeResponse.data[0].ja.address1,
                destinationAddress3: zipCodeResponse.data[0].ja.address2,
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        }
    }

    async getOverseaAddressFromZipCode(zipCode: string): Promise<void> {
        const zipCodeResponse = await this.getAddressFromZipcode(zipCode, 'contactZipCode')
        if (zipCodeResponse && zipCodeResponse.data
            && zipCodeResponse.data[0] && zipCodeResponse.data[0].ja) {
            this.setState({
                contactPrefecture: zipCodeResponse.data[0].ja.prefecture,
                contactAddress2: zipCodeResponse.data[0].ja.address1,
                contactAddress3: zipCodeResponse.data[0].ja.address2,
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        }
    }

    async getAddressFromZipcode(zipCode: string, resultCode: string): Promise<NatZipCode | void> {
        await this.props.eventEmitter.showLoading()
        try {
            const firstPart = zipCode.substr(0, 3)
            const lastPart = zipCode.substr(3, zipCode.length - 1)
            return this.props.condominiumRepo.getZipCode(firstPart, lastPart).then(
                zipData => {
                    const zipCodeResponse: NatZipCode = zipData
                    if (zipCodeResponse && zipCodeResponse.data
                        && zipCodeResponse.data.length > 1) {
                        // 郵便番号から取得できた住所が複数存在する場合
                        const requestItemError = new RequestItemError(
                            resultCode, Messages.WCONA0002.toString(), 'WCONA0002')
                        this.props.eventEmitter.emit(requestItemError.resultCode, requestItemError)
                    }
                    return zipCodeResponse
                },
            ).catch(
                () => {
                    // 郵便番号から住所が取得できなかった場合
                    const requestItemError = new RequestItemError(
                        resultCode, Messages.ECONA0001.toString(), 'ECONA0001')
                    this.props.eventEmitter.emit(requestItemError.resultCode, requestItemError)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    renderApplicantForm(): ReactElement {
        const {applicantFamilyName, applicantFirstName} = this.state

        const applicantRoom = NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_RIDGENAME) + ' ' +
            convertToRoomNumberForResident(
                NatResidentLoginInfoSessionStorageHelper
                    .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER),
            )

        return (
            <>
                <ItemHeading>
                    申請者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFamilyName',
                                        maxLength: 10,
                                        value: applicantFamilyName,
                                        placeholder: '例：素敵',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFamilyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFirstName',
                                        maxLength: 10,
                                        placeholder: '例：太郎',
                                        value: applicantFirstName,
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFirstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderAbsenceDateForm(): ReactElement {
        const {absenceStartDate, absenceEndDate, currentDate, holidays} = this.state
        const maxOfStartDate = absenceEndDate ? new Date(absenceEndDate) : null
        const minOfEndDate = absenceStartDate ? new Date(absenceStartDate) : null

        return (
            <>
                <ItemHeading>
                    不在期間
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'開始日'}
                        required
                        component={
                            <PPDatePickerForResident
                                date={absenceStartDate}
                                onChange={this.handleChangeAbsenceStartDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['absenceStartDate']}
                                eventEmitter={this.props.eventEmitter}
                                today={currentDate.toDate()}
                                maxDate={maxOfStartDate}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'終了日'}
                        required
                        component={
                            <PPDatePickerForResident
                                date={absenceEndDate}
                                onChange={this.handleChangeAbsenceEndDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['absenceEndDate']}
                                eventEmitter={this.props.eventEmitter}
                                minDate={minOfEndDate}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderApplicantContactInfoForm(): ReactElement {
        const {applicantContactPhoneNumber} = this.state

        return (
            <>
                <ItemHeading>
                    不在中の連絡先（本人）
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>電話番号<BreakPcOnly/>（ハイフン不要）</span>}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        maxLength: 11,
                                        name: 'applicantContactPhoneNumber',
                                        value: applicantContactPhoneNumber,
                                        placeholder: '例：0312345678',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantContactPhoneNumber']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderDestinationTypeForm(): ReactElement {
        const {destinationType} = this.state

        return (
            <>
                <ItemHeading>
                    滞在先
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <DisplayItemInputTitleRequiredNoMargin>
                        {'国内・国外を選択'}
                    </DisplayItemInputTitleRequiredNoMargin>
                    <Box height={'20px'}/>
                    <GridContainer>
                        <GridItem over={12}>
                            <PPRadioGroupForResident
                                data={DESTINATION_TYPE.values()}
                                selectValue={destinationType}
                                onChange={this.handleChangeDestinationType}
                            />
                        </GridItem>
                    </GridContainer>
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderDomesticDestinationForm(): ReactElement {
        const {
            destinationZipCode, destinationPrefecture, destinationAddress2,
            destinationAddress3, destinationBuildingRoomNumber,
        } = this.state

        return (
            <>
                <ItemHeading>
                    滞在先の住所
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'郵便番号（ハイフン不要）'}
                        component={
                            <ErrorMessageAreaWrapper>
                                <FlexContainer>
                                    <Box flex={2} minWidth={0}>
                                        <PPTextFieldForResident
                                            inputProps={{
                                                maxLength: 7,
                                                name: 'destinationZipCode',
                                                value: destinationZipCode,
                                                placeholder: '例：1234567',
                                            }}
                                            onChange={this.handleChangeText}
                                            eventKeys={['destinationZipCode']}
                                            eventEmitter={this.props.eventEmitter}
                                        />
                                    </Box>
                                    <FlexContainer>
                                        <BlockSpacer width={5}/>
                                    </FlexContainer>
                                    <Box flex={1} maxWidth={'160px'} minWidth={'125px'} maxHeight={'50px'}>
                                        <DefaultStyledButton
                                            onClick={() => this.getDomesticAddressFromZipCode(destinationZipCode)}>
                                            住所自動入力
                                        </DefaultStyledButton>
                                    </Box>
                                </FlexContainer>
                            </ErrorMessageAreaWrapper>
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'都道府県'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 4,
                                    name: 'destinationPrefecture',
                                    value: destinationPrefecture,
                                    placeholder: '例：東京都',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['destinationPrefecture']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'市区町村'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'destinationAddress2',
                                    value: destinationAddress2,
                                    placeholder: '例：港区',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['destinationAddress2']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'番地'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'destinationAddress3',
                                    value: destinationAddress3,
                                    placeholder: '例：芝４－６－６',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['destinationAddress3']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'建物名・部屋番号'}
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'destinationBuildingRoomNumber',
                                    value: destinationBuildingRoomNumber,
                                    placeholder: '例：すてきマンション　１０１号室',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['destinationBuildingRoomNumber']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderOverseaDestinationForm(): ReactElement {
        const {
            destinationAddress,
        } = this.state

        return (
            <>
                <ItemHeading>
                    滞在先の住所
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'住所'}
                        component={
                            <PPTextAreaForResident
                                inputProps={{
                                    maxLength: 200,
                                    name: 'destinationAddress',
                                    value: destinationAddress,
                                    placeholder: '200文字以内',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['destinationAddress']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
                <Box height={'15px'}/>
            </>
        )
    }

    renderContactForm(): ReactElement {
        const {
            contactFamilyName, contactFirstName, contactRelationship, contactZipCode,
            contactPrefecture, contactAddress2, contactAddress3, contactBuildingRoomNumber, contactPhoneNumber,
        } = this.state

        return (
            <>
                <ItemHeading>
                    国内の連絡先
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'contactFamilyName',
                                        maxLength: 10,
                                        value: contactFamilyName,
                                        placeholder: '例：素敵',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['contactFamilyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'contactFirstName',
                                        maxLength: 10,
                                        placeholder: '例：太郎',
                                        value: contactFirstName,
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['contactFirstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'続柄'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 15,
                                    name: `contactRelationship`,
                                    value: contactRelationship,
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contactRelationship']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <GridContainer>
                        <GridItem over={3}/>
                        <GridItem over={9}>
                            <RelationShipNode>
                                例：夫、妻、子など
                            </RelationShipNode>
                        </GridItem>
                    </GridContainer>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'郵便番号（ハイフン不要）'}
                        component={
                            <ErrorMessageAreaWrapper>
                                <FlexContainer>
                                    <Box flex={2} minWidth={0}>
                                        <PPTextFieldForResident
                                            inputProps={{
                                                maxLength: 7,
                                                name: 'contactZipCode',
                                                value: contactZipCode,
                                                placeholder: '例：1234567',
                                            }}
                                            onChange={this.handleChangeText}
                                            eventKeys={['contactZipCode']}
                                            eventEmitter={this.props.eventEmitter}
                                        />
                                    </Box>
                                    <FlexContainer>
                                        <BlockSpacer width={5}/>
                                    </FlexContainer>
                                    <Box flex={1} maxWidth={'160px'} minWidth={'125px'} maxHeight={'50px'}>
                                        <DefaultStyledButton
                                            onClick={() => this.getOverseaAddressFromZipCode(contactZipCode)}>
                                            住所自動入力
                                        </DefaultStyledButton>
                                    </Box>
                                </FlexContainer>
                            </ErrorMessageAreaWrapper>
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'都道府県'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 4,
                                    name: 'contactPrefecture',
                                    value: contactPrefecture,
                                    placeholder: '例：東京都',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contactPrefecture']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'市区町村'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'contactAddress2',
                                    value: contactAddress2,
                                    placeholder: '例：港区',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contactAddress2']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'番地'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'contactAddress3',
                                    value: contactAddress3,
                                    placeholder: '例：芝４－６－６',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contactAddress3']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'建物名・部屋番号'}
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'contactBuildingRoomNumber',
                                    value: contactBuildingRoomNumber,
                                    placeholder: '例：すてきマンション　１０１号室',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contactBuildingRoomNumber']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>電話番号<BreakPcOnly/>（ハイフン不要）</span>}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 11,
                                    name: 'contactPhoneNumber',
                                    value: contactPhoneNumber,
                                    placeholder: '例：0312345678',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contactPhoneNumber']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {destinationType, applyButtonDisabled} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                {this.renderApplicantForm()}
                < Box height={'15px'}/>
                {this.renderAbsenceDateForm()}
                < Box height={'15px'}/>
                {this.renderApplicantContactInfoForm()}
                <Box height={'15px'}/>
                {this.renderDestinationTypeForm()}
                <Box height={'15px'}/>
                {destinationType === DESTINATION_TYPE.DOMESTIC.value &&
                    this.renderDomesticDestinationForm()
                }
                {destinationType === DESTINATION_TYPE.OVERSEA.value && (
                    <>
                        {this.renderOverseaDestinationForm()}
                        {this.renderContactForm()}
                    </>
                )
                }
                <ApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}

const RelationShipNode = styled(FlexContainer)`
  font-size: ${FontSize.Small};
  color: ${Colors.AccentDark};
  margin-top: 5px;
`

const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`
